import { CheckIcon } from 'assets/icons';
import { CREATE_VAULT_STEP_COUNT, CreateVaultSteps } from 'common/enums';
import { classnames } from 'helpers/utils';
import styles from './progress-steps.module.scss';

type ProgressStepsProps = {
  step: CreateVaultSteps;
  onStepSelect: (step: CreateVaultSteps) => void;
};

const STEP_LABELS: Record<CreateVaultSteps, string> = {
  [CreateVaultSteps.information]: 'Details',
  [CreateVaultSteps.plan]: 'Plan',
  [CreateVaultSteps.confirmation]: 'Review',
};

const STEPS = Object.values(CreateVaultSteps).filter(
  (value): value is CreateVaultSteps => typeof value === 'number',
);

// "Step 2 of 3 · Plan" plus a dot per step — the minimal progress indicator
// from the design. Completed steps are clickable so the user can jump back to
// one; the current and not-yet-reached steps are inert, since skipping ahead
// would bypass each step's validation.
const ProgressSteps: React.FC<ProgressStepsProps> = ({
  step,
  onStepSelect,
}) => (
  <div className={styles.container}>
    <span className={styles.label}>
      {`Step ${step} of ${CREATE_VAULT_STEP_COUNT} · ${STEP_LABELS[step]}`}
    </span>

    <ol className={styles.dots}>
      {STEPS.map((stepNumber) => {
        const isComplete = stepNumber < step;
        const isCurrent = stepNumber === step;

        const dotContent = (
          <>
            {isComplete ? (
              <CheckIcon className={styles.check} />
            ) : (
              <span className={styles.inner} />
            )}
            <span className={styles.srOnly}>{STEP_LABELS[stepNumber]}</span>
          </>
        );

        return (
          <li key={stepNumber} aria-current={isCurrent ? 'step' : undefined}>
            {isComplete ? (
              <button
                type="button"
                className={classnames(styles.dot, styles.dotComplete)}
                title={`Back to ${STEP_LABELS[stepNumber]}`}
                onClick={() => onStepSelect(stepNumber)}
              >
                {dotContent}
              </button>
            ) : (
              <span
                className={classnames(styles.dot, {
                  [styles.dotCurrent]: isCurrent,
                })}
              >
                {dotContent}
              </span>
            )}
          </li>
        );
      })}
    </ol>
  </div>
);

export { ProgressSteps };
